import { baseTheme } from '@src/theme';
import { EButtonIntents } from '@src/typings';
import styled, { css, DefaultTheme } from 'styled-components';
import { IconButtonProps } from './types';

type StyledButtonProps = Pick<
    IconButtonProps,
    'intent' | 'rounded' | 'size' | 'disabled'
> & {
    theme?: DefaultTheme;
};

const getButtonSize = (size: string) => {
    switch (size) {
        case 'sm':
            return css`
                padding: 0.5rem;
            `;
        case 'lg':
            return css`
                padding: 1rem;
            `;
        default:
            return css`
                padding: 0.75rem;
            `;
    }
};

const getButtonRadius = (rounded: string) => {
    switch (rounded) {
        case 'full':
            return css`
                border-radius: 9999px;
            `;
        case 'lg':
            return css`
                border-radius: 0.5rem;
            `;
        default:
            return css`
                border-radius: 0.25rem;
            `;
    }
};

const getButtonIntent = (props: StyledButtonProps) => {
    switch (props.intent) {
        case EButtonIntents.Filled:
            return css`
                background-color: ${props.theme?.colors?.primary?.main
                    ?.filled || baseTheme.colors.primary.main.filled};
                color: ${props.theme?.colors?.primary?.main?.text ||
                baseTheme.colors.primary.main.text};
                border: none;

                &:hover:not(:disabled) {
                    background-color: ${props.theme?.colors?.primary?.main
                        ?.hover || baseTheme.colors.primary.main.hover};
                }
            `;
        case EButtonIntents.LightFilled:
            return css`
                background-color: ${props.theme?.colors?.primary?.light
                    ?.filled || baseTheme.colors.primary.light.filled};
                color: ${props.theme?.colors?.primary?.light?.text ||
                baseTheme.colors.primary.light.text};
                border: none;

                &:hover:not(:disabled) {
                    background-color: ${props.theme?.colors?.primary?.light
                        ?.hover || baseTheme.colors.primary.light.hover};
                }
            `;
        case EButtonIntents.Linear:
            return css`
                background-color: transparent;
                color: ${props.theme?.colors?.primary?.light?.text ||
                baseTheme.colors.primary.light.text};
                border: 1px solid
                    ${props.theme?.colors?.primary?.main?.border ||
                    baseTheme.colors.primary.main.border};

                &:hover:not(:disabled) {
                    background-color: ${props.theme?.colors?.primary?.main
                        ?.linearHover ||
                    baseTheme.colors.primary.main.linearHover};
                }
            `;
        case EButtonIntents.LightLinear:
            return css`
                background-color: transparent;
                color: ${props.theme?.colors?.secondary?.light?.text ||
                baseTheme.colors.secondary.light.text};
                border: 1px solid
                    ${props.theme?.colors?.secondary?.light?.border ||
                    baseTheme.colors.secondary.light.border};

                &:hover:not(:disabled) {
                    background-color: ${props.theme?.colors?.secondary?.light
                        ?.surfaceHover ||
                    baseTheme.colors.secondary.light.surfaceHover};
                }
            `;
        case EButtonIntents.Text:
            return css`
                background-color: transparent;
                color: ${props.theme?.colors?.primary?.main?.text ||
                baseTheme.colors.primary.main.text};
                border: none;

                &:hover:not(:disabled) {
                    background-color: ${props.theme?.colors?.primary?.light
                        ?.filled || baseTheme.colors.primary.light.filled};
                }
            `;
        default:
            return '';
    }
};

export const StyledIconButton = styled.button<StyledButtonProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    ${(props) => getButtonSize(props.size || 'md')}
    ${(props) => getButtonRadius(props.rounded || 'lg')}
  ${(props) => getButtonIntent(props)}

  &:disabled {
        cursor: not-allowed;
        opacity: 0.25;
    }

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    svg {
        width: 1rem;
        height: 1rem;
    }
`;
